import { useCallback, useMemo } from "react";
import { Box, Checkbox, Group, Text } from "@mantine/core";
import MaterialPickerSpecimenRow, {
  SpecimenInfo,
} from "./MaterialPickerSpecimenRow";

export type { SpecimenInfo } from "./MaterialPickerSpecimenRow";
export type { BlockInfo } from "./MaterialPickerBlockRow";
export type { SlideInfo } from "./MaterialPickerSlideRow";

export interface MaterialPickerSelection {
  blockIds: Set<string>;
  slideIds: Set<string>;
}

interface MaterialPickerProps {
  specimens: SpecimenInfo[];
  selection: MaterialPickerSelection;
  onChange: (next: MaterialPickerSelection) => void;
  error?: string | null;
  helperText?: string;
  emptyMessage?: string;
}

export default function MaterialPicker({
  specimens,
  selection,
  onChange,
  error,
  helperText,
  emptyMessage = "This case has no blocks or slides to select.",
}: MaterialPickerProps) {
  const { blockIds: selectedBlockIds, slideIds: selectedSlideIds } = selection;

  const allSlides = useMemo(() => {
    const slides: Array<{ id: string }> = [];
    specimens.forEach((spec) => {
      spec.blocks?.forEach((block) => {
        block.slides?.forEach((slide) => slides.push(slide));
      });
    });
    return slides;
  }, [specimens]);

  const allBlocks = useMemo(() => {
    const blocks: Array<{ id: string }> = [];
    specimens.forEach((spec) => {
      spec.blocks?.forEach((block) => blocks.push(block));
    });
    return blocks;
  }, [specimens]);

  const blockSlideMap = useMemo(() => {
    const map = new Map<string, string[]>();
    specimens.forEach((spec) => {
      spec.blocks?.forEach((block) => {
        map.set(block.id, block.slides?.map((s) => s.id) || []);
      });
    });
    return map;
  }, [specimens]);

  const specimenBlockMap = useMemo(() => {
    const map = new Map<string, string[]>();
    specimens.forEach((spec) => {
      map.set(spec.id, spec.blocks?.map((b) => b.id) || []);
    });
    return map;
  }, [specimens]);

  const isSpecimenChecked = useCallback(
    (specimenId: string) => {
      const bIds = specimenBlockMap.get(specimenId) || [];
      if (bIds.length === 0) return false;
      return bIds.every((bId) => {
        const blockSelected = selectedBlockIds.has(bId);
        const sIds = blockSlideMap.get(bId) || [];
        const allSlidesSelected =
          sIds.length > 0 && sIds.every((sId) => selectedSlideIds.has(sId));
        return blockSelected && (sIds.length === 0 || allSlidesSelected);
      });
    },
    [specimenBlockMap, selectedBlockIds, blockSlideMap, selectedSlideIds]
  );

  const isSpecimenIndeterminate = useCallback(
    (specimenId: string) => {
      if (isSpecimenChecked(specimenId)) return false;
      const bIds = specimenBlockMap.get(specimenId) || [];
      return bIds.some((bId) => {
        if (selectedBlockIds.has(bId)) return true;
        const sIds = blockSlideMap.get(bId) || [];
        return sIds.some((sId) => selectedSlideIds.has(sId));
      });
    },
    [
      isSpecimenChecked,
      specimenBlockMap,
      selectedBlockIds,
      blockSlideMap,
      selectedSlideIds,
    ]
  );

  const allAreSelected = useMemo(() => {
    const slidesSelected =
      allSlides.length > 0 &&
      allSlides.every((s) => selectedSlideIds.has(s.id));
    const blocksSelected =
      allBlocks.length > 0 &&
      allBlocks.every((b) => selectedBlockIds.has(b.id));

    if (allSlides.length > 0 && allBlocks.length > 0)
      return slidesSelected && blocksSelected;
    if (allSlides.length > 0) return slidesSelected;
    if (allBlocks.length > 0) return blocksSelected;
    return false;
  }, [allSlides, selectedSlideIds, allBlocks, selectedBlockIds]);

  const someAreSelected = useMemo(
    () =>
      (selectedSlideIds.size > 0 || selectedBlockIds.size > 0) &&
      !allAreSelected,
    [selectedSlideIds, selectedBlockIds, allAreSelected]
  );

  const summary = useMemo(() => {
    const slideCount = selectedSlideIds.size;
    const explicitBlockCount = selectedBlockIds.size;
    if (slideCount === 0 && explicitBlockCount === 0) return "";

    const blocksWithSelectedSlides = new Set<string>();
    specimens.forEach((spec) => {
      spec.blocks?.forEach((block) => {
        block.slides?.forEach((slide) => {
          if (selectedSlideIds.has(slide.id)) {
            blocksWithSelectedSlides.add(block.id);
          }
        });
      });
    });
    const blocksCountWithSlides = blocksWithSelectedSlides.size;

    if (explicitBlockCount > 0 && slideCount > 0) {
      return `${explicitBlockCount} block${
        explicitBlockCount !== 1 ? "s" : ""
      } and ${slideCount} slide${slideCount !== 1 ? "s" : ""} selected`;
    }
    if (explicitBlockCount > 0) {
      return `${explicitBlockCount} block${
        explicitBlockCount !== 1 ? "s" : ""
      } selected`;
    }
    return `${slideCount} slide${
      slideCount !== 1 ? "s" : ""
    } selected across ${blocksCountWithSlides} block${
      blocksCountWithSlides !== 1 ? "s" : ""
    }`;
  }, [selectedSlideIds, selectedBlockIds, specimens]);

  const toggleSlide = useCallback(
    (slideId: string) => {
      const nextSlides = new Set(selectedSlideIds);
      if (nextSlides.has(slideId)) {
        nextSlides.delete(slideId);
      } else {
        nextSlides.add(slideId);
      }
      onChange({ blockIds: new Set(selectedBlockIds), slideIds: nextSlides });
    },
    [selectedSlideIds, selectedBlockIds, onChange]
  );

  const toggleBlock = useCallback(
    (blockId: string) => {
      const nextBlocks = new Set(selectedBlockIds);
      if (nextBlocks.has(blockId)) {
        nextBlocks.delete(blockId);
      } else {
        nextBlocks.add(blockId);
      }

      onChange({ blockIds: nextBlocks, slideIds: new Set(selectedSlideIds) });
    },
    [selectedBlockIds, selectedSlideIds, onChange]
  );

  const toggleSpecimen = useCallback(
    (specimenId: string) => {
      const bIds = specimenBlockMap.get(specimenId) || [];
      const isChecked = isSpecimenChecked(specimenId);

      const nextBlocks = new Set(selectedBlockIds);
      const nextSlides = new Set(selectedSlideIds);

      bIds.forEach((bId) => {
        const sIds = blockSlideMap.get(bId) || [];
        if (isChecked) {
          nextBlocks.delete(bId);
          sIds.forEach((sId) => nextSlides.delete(sId));
        } else {
          nextBlocks.add(bId);
          sIds.forEach((sId) => nextSlides.add(sId));
        }
      });

      onChange({ blockIds: nextBlocks, slideIds: nextSlides });
    },
    [
      specimenBlockMap,
      isSpecimenChecked,
      selectedBlockIds,
      selectedSlideIds,
      blockSlideMap,
      onChange,
    ]
  );

  const handleSelectAllChange = useCallback(() => {
    if (allAreSelected) {
      onChange({ blockIds: new Set(), slideIds: new Set() });
    } else {
      onChange({
        blockIds: new Set(allBlocks.map((b) => b.id)),
        slideIds: new Set(allSlides.map((s) => s.id)),
      });
    }
  }, [allAreSelected, allBlocks, allSlides, onChange]);

  const isBlockFullySelected = useCallback(
    (blockId: string) => selectedBlockIds.has(blockId),
    [selectedBlockIds]
  );

  const isBlockPartiallySelected = useCallback(() => false, []);

  if (specimens.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <Box>
      <Group justify="space-between" mb="xs">
        {summary ? (
          <Text size="xs" c="dimmed">
            {summary}
          </Text>
        ) : (
          <span />
        )}
        {helperText && (
          <Text size="xs" c="dimmed" fs="italic">
            {helperText}
          </Text>
        )}
      </Group>
      {error && (
        <Text size="xs" c="red" mb="xs">
          {error}
        </Text>
      )}
      <Checkbox
        label="Select All"
        size="sm"
        fw={600}
        mb="xs"
        checked={allAreSelected}
        indeterminate={someAreSelected}
        onChange={handleSelectAllChange}
      />
      {specimens.map((specimen) => (
        <MaterialPickerSpecimenRow
          key={specimen.id}
          specimen={specimen}
          selectedSlideIds={selectedSlideIds}
          isAllSelected={isSpecimenChecked(specimen.id)}
          isPartiallySelected={isSpecimenIndeterminate(specimen.id)}
          isBlockFullySelected={isBlockFullySelected}
          isBlockPartiallySelected={isBlockPartiallySelected}
          onToggleSpecimen={toggleSpecimen}
          onToggleBlock={toggleBlock}
          onToggleSlide={toggleSlide}
        />
      ))}
    </Box>
  );
}
